import { useCallback, useEffect, useState } from "react";
import { profileApi } from "../api";
import { useGlobalNotifications } from "../hooks/use-global-notifications";
import { BiometricsStorage } from "../helpers/biometrics-storage";

export interface UserSettings {
  autoPromptPasskey: boolean;
}

type UseUserSettingsArgs = {
  initialAutoPromptPasskey?: boolean;
};

export const useUserSettings = (
  { initialAutoPromptPasskey = true }: UseUserSettingsArgs = {},
) => {
  const { showError, showSuccess } = useGlobalNotifications();
  const [settings, setSettings] = useState<UserSettings>({
    autoPromptPasskey: initialAutoPromptPasskey,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Sync with prefetched initial data from context
  useEffect(() => {
    setSettings({ autoPromptPasskey: initialAutoPromptPasskey });
  }, [initialAutoPromptPasskey]);

  // Load user settings from profile
  const loadSettings = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const profile = await profileApi.getMe();

      // Update settings with profile data
      setSettings({
        autoPromptPasskey: profile.auto_prompt_passkey ?? true,
      });
    } catch (err: any) {
      const errorMsg = err?.message || "Failed to load settings";
      setError(errorMsg);
      console.error("Failed to load user settings:", err);
      showError(errorMsg);
    } finally {
      setLoading(false);
    }
  }, []);

  // Update auto-prompt passkey setting
  const updateAutoPromptPasskey = useCallback(async (enabled: boolean) => {
    try {
      setError(null);

      setSettings((prev) => ({ ...prev, autoPromptPasskey: enabled }));

      // Call API to update profile
      await profileApi.updateMe({ auto_prompt_passkey: enabled });

      // Sync to localStorage so login page knows the preference
      BiometricsStorage.setAutoPromptPasskey(enabled);

      showSuccess(enabled ? "Auto-prompt enabled" : "Auto-prompt disabled");
    } catch (err: any) {
      const errorMsg = err?.message || "Failed to update setting";
      setError(errorMsg);
      showError(errorMsg);

      setSettings((prev) => ({ ...prev, autoPromptPasskey: !enabled }));
      throw err;
    }
  }, [showError, showSuccess]);

  return {
    settings,
    loading,
    error,
    updateAutoPromptPasskey,
    refreshSettings: loadSettings,
  };
};
